import { useEffect, useSyncExternalStore } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@supabase/supabase-js";

export type AppRole = "super_admin" | "admin" | "moderator";

export interface AdminProfile {
  id: string;
  user_id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  is_active: boolean;
}

interface AuthState {
  session: Session | null;
  user: User | null;
  profile: AdminProfile | null;
  roles: AppRole[];
  loading: boolean;
  error: string | null;
  isStaff: boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;
}

// ---- Global singleton store (one listener for the whole app) ----
let state: AuthState = {
  session: null,
  user: null,
  profile: null,
  roles: [],
  loading: true,
  error: null,
  isStaff: false,
  isSuperAdmin: false,
  isAdmin: false,
};

const listeners = new Set<() => void>();
const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};
const getSnapshot = () => state;
const emit = () => listeners.forEach((l) => l());

const setState = (patch: Partial<AuthState>) => {
  const next = { ...state, ...patch };
  next.isStaff = next.roles.length > 0;
  next.isSuperAdmin = next.roles.includes("super_admin");
  next.isAdmin = next.isSuperAdmin || next.roles.includes("admin");
  state = next;
  emit();
};

let loadedForUserId: string | null = null;
let inflight: Promise<void> | null = null;

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

const isJwtError = (err: { message?: string; code?: string } | null | undefined): boolean => {
  if (!err) return false;
  const msg = (err.message ?? "").toLowerCase();
  const code = (err.code ?? "").toLowerCase();
  return (
    msg.includes("jwt") ||
    msg.includes("sub claim") ||
    msg.includes("invalid claim") ||
    msg.includes("invalid token") ||
    code === "pgrst301" ||
    code === "401" ||
    code === "403" ||
    code === "bad_jwt"
  );
};

const forceSignOut = async (reason: string) => {
  console.warn(`[useAdminAuth] forcing sign-out: ${reason}`);
  loadedForUserId = null;
  try {
    await supabase.auth.signOut();
  } catch {
    /* ignore */
  }
  try {
    // Wipe any stale tokens left in storage by older Supabase clients.
    Object.keys(localStorage)
      .filter((k) => k.startsWith("sb-") && k.endsWith("-auth-token"))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
  setState({
    session: null,
    user: null,
    profile: null,
    roles: [],
    loading: false,
    error: null,
  });
};

const loadProfileAndRoles = async (userId: string): Promise<void> => {
  if (loadedForUserId === userId) return;
  if (inflight) return inflight;

  inflight = (async () => {
    let lastErrorMsg: string | null = null;
    // Up to 8 attempts (~9s) to absorb JWT propagation lag right after sign-in.
    for (let attempt = 0; attempt < 8; attempt += 1) {
      // Ensure the freshly-issued JWT is attached to subsequent requests.
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        setState({ profile: null, roles: [], loading: false, error: null });
        break;
      }

      const [profileRes, rolesRes] = await Promise.all([
        supabase.from("admin_profiles").select("*").eq("user_id", userId).maybeSingle(),
        supabase.from("user_roles").select("role").eq("user_id", userId),
      ]);

      const err = profileRes.error ?? rolesRes.error;
      if (err) {
        lastErrorMsg = err.message;
        console.warn(`[useAdminAuth] attempt ${attempt + 1} failed:`, err);
        // Stale/invalid JWT: signing out is the only way to recover.
        if (isJwtError(err)) {
          await forceSignOut(`jwt error: ${err.message}`);
          inflight = null;
          return;
        }
        await wait(400 * (attempt + 1));
        continue;
      }

      const roles = ((rolesRes.data ?? []) as { role: AppRole }[]).map((r) => r.role);
      // Empty roles right after sign-in can mean JWT not yet propagated to PostgREST.
      // Retry a few times before giving up.
      if (roles.length === 0 && attempt < 4) {
        await wait(400 * (attempt + 1));
        continue;
      }

      const profile = (profileRes.data as AdminProfile) ?? null;
      loadedForUserId = userId;
      setState({ profile, roles, loading: false, error: null });
      inflight = null;
      return;
    }

    setState({
      loading: false,
      error: lastErrorMsg ?? "Impossible de vérifier les droits administrateur.",
    });
    inflight = null;
  })();

  return inflight;
};

let initialized = false;
const initAuth = () => {
  if (initialized) return;
  initialized = true;

  // 1. Listener FIRST (sync only inside)
  supabase.auth.onAuthStateChange((_event, newSession) => {
    const prevUserId = state.user?.id ?? null;
    const newUserId = newSession?.user?.id ?? null;

    setState({ session: newSession, user: newSession?.user ?? null });

    if (!newSession) {
      loadedForUserId = null;
      setState({ profile: null, roles: [], loading: false, error: null });
      return;
    }

    // On any fresh sign-in event, clear stale errors and refetch if needed.
    if (newUserId !== prevUserId || loadedForUserId !== newUserId) {
      loadedForUserId = null;
      setState({ loading: true, error: null });
      setTimeout(() => {
        void loadProfileAndRoles(newUserId!);
      }, 0);
    }
  });

  // 2. THEN fetch existing session
  supabase.auth.getSession().then(({ data: { session } }) => {
    setState({ session, user: session?.user ?? null });
    if (!session) {
      setState({ loading: false });
    } else if (loadedForUserId !== session.user.id) {
      void loadProfileAndRoles(session.user.id);
    } else {
      setState({ loading: false });
    }
  });
};

export function useAdminAuth(): AuthState {
  // Initialize once (lazy, on first hook usage)
  useEffect(() => {
    initAuth();
  }, []);
  // Also init synchronously in case something reads state immediately
  initAuth();

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Force a fresh fetch of admin profile + roles (e.g. after a failed verification). */
export async function reloadAdminProfile(): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    setState({ profile: null, roles: [], loading: false, error: null });
    return;
  }
  loadedForUserId = null;
  setState({ loading: true, error: null });
  await loadProfileAndRoles(session.user.id);
}

/** Clear any cached verification error (used when user retries). */
export function clearAdminAuthError(): void {
  if (state.error) setState({ error: null });
}

/** Convertit un username en email synthétique interne (login sans email réel). */
export function usernameToEmail(username: string): string {
  return `${username.trim().toLowerCase()}@admin.conceptions.local`;
}
